import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import authTokenUtil from '../account/authTokenUtil';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import SlabLogo from '../components/SlabLogo';
import type { MessageEmbedAuthToken } from '../constants/messages';
import { MESSAGE_EMBED_AUTH_TOKEN } from '../constants/messages';
import QuillComponent from '../editor/QuillComponent';
import QuillContext from '../editor/QuillContext';
import tocIcon from '../icons/tableOfContents.svg';
import PageMeta from '../utils/PageMeta';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import postPath, { postEmbedPath } from '../utils/url/postPath';
import styles from './EmbeddedPostView.module.scss';
import PostSkeleton from './PostSkeleton';
import ConnectedTableOfContents from './actionButtons/ConnectedTableOfContents';
import TableOfContents from './actionButtons/TableOfContents';
import replacePostPath from './utils/replacePostPath';
import { IsPublicViewProvider } from '../utils/useIsPublicView';
import {
  AllowAccessPostViewError,
  NotLoggedInPostViewError,
  RequestAccessPostViewError,
} from './PostViewError';
import { gql } from '../graphql/generated/gql';

const EDITOR_MODULE_CONFIG = {
  headerCollapse: true,
  scrollToAnchor: true,
};

gql(`
  fragment EmbeddedPostView_post on PublicPostFields {
    id
    title
    content
    version
    ...postPath_post
  }
`);

const POST_QUERY = gql(`
  query embeddedPost($id: ID!) {
    post(id: $id, view: true) {
      ...EmbeddedPostView_post
    }
  }
`);

const PUBLIC_POST_QUERY = gql(`
  query publicPost($id: ID!) {
    post: publicPost(id: $id) {
      ...EmbeddedPostView_post
    }
  }
`);

const EmbeddedNotFoundView = () => {
  const [postId] = useSlug('postSlug');
  const isInIframe = window.self !== window.top;
  const hasToken = !!authTokenUtil.get();

  useEffect(() => {
    // Use BroadcastChannel to communicate with other iframes on the same site
    // Can't replace postMessage since BC could be blocked/partitioned
    const sameTopLevelBC = new BroadcastChannel(MESSAGE_EMBED_AUTH_TOKEN);
    sameTopLevelBC.onmessage = () => {
      window.location.reload();
    };

    const postMessageListener = (
      event: MessageEvent<MessageEmbedAuthToken>,
    ) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === MESSAGE_EMBED_AUTH_TOKEN && event.data.token) {
        authTokenUtil.set(event.data.token).then(() => {
          sameTopLevelBC.postMessage({});
          window.location.reload();
        });

        if (event.source && 'close' in event.source) {
          event.source.close();
        }
      }
    };

    window.addEventListener('message', postMessageListener);

    return () => {
      sameTopLevelBC.close();
      window.removeEventListener('message', postMessageListener);
    };
  }, []);

  if (hasToken) {
    return <RequestAccessPostViewError postId={postId} />;
  }

  if (isInIframe) {
    return <AllowAccessPostViewError />;
  }

  return <NotLoggedInPostViewError postId={postId} />;
};

const EmbeddedPostView = () => {
  const [postId, name] = useSlug('postSlug');
  const [isShowingTOC, setIsShowingTOC] = useState(false);
  const hasToken = !!authTokenUtil.get();

  const embeddedPostResult = useQuery(POST_QUERY, {
    variables: { id: postId },
    skip: !hasToken,
  });

  const publicPostResult = useQuery(PUBLIC_POST_QUERY, {
    variables: { id: postId },
    skip: hasToken,
  });

  const { data, error, loading } = hasToken
    ? embeddedPostResult
    : publicPostResult;

  const post = data?.post;

  const { title, content } = post ?? {};
  const navigate = useNavigate();

  useEffect(() => {
    if (title) {
      replacePostPath(postEmbedPath({ id: postId, title }), navigate);
    }
  }, [navigate, postId, title]);

  useEffect(() => {
    if (!loading && post) {
      const { hash } = document.location;
      if (hash) {
        const header = document.getElementById(hash.slice(1));
        if (header != null) {
          header.scrollIntoView();
        }
      }
    }
  }, [loading, post]);

  if (error) {
    return <EmbeddedNotFoundView />;
  }

  return (
    <IsPublicViewProvider publicView={!hasToken}>
      <div className={styles.container}>
        <div
          id="content"
          className={classNames('no-padding-x', 'no-padding-y', {
            [styles.isShowingTOC]: isShowingTOC,
          })}
        >
          <PageMeta title={`${title || _.startCase(name)} - Slab`} />
          {post ? (
            <QuillContext>
              <div className={styles.mainContent}>
                <div className={styles.postContainer}>
                  <QuillComponent
                    namespace="embedded"
                    content={content}
                    moduleConfig={EDITOR_MODULE_CONFIG}
                    readOnly
                  />
                </div>
              </div>
              <div className={styles.tocContainer}>
                <ConnectedTableOfContents postId={postId}>
                  {(tocProps) => (
                    <>
                      <h4 className={styles.tocTitle}>Table of Contents</h4>
                      <TableOfContents {...tocProps} />
                    </>
                  )}
                </ConnectedTableOfContents>
              </div>
              <div className={styles.footer}>
                <a
                  href={postPath(post)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SlabLogo className={styles.logo} />
                </a>
                <Button
                  className={styles.tocButton}
                  onClick={() => {
                    setIsShowingTOC(!isShowingTOC);
                  }}
                >
                  <SVGIcon size={12}>{tocIcon}</SVGIcon>
                </Button>
              </div>
            </QuillContext>
          ) : (
            <div className={styles.postContainer}>
              <PostSkeleton />
            </div>
          )}
        </div>
      </div>
    </IsPublicViewProvider>
  );
};

export default EmbeddedPostView;
